import React from 'react';
import Container from 'components/atomic/atoms/container';
import FlexContainer from 'components/atomic/atoms/flex-container';
import styles from './sticky-footer.module.scss';

export interface StickyFooterProps {
  /** Primary action slot (left side) */
  primaryAction: React.ReactNode;
  /** Secondary actions slot (right side) */
  secondaryActions?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * StickyFooter - Atomic sticky footer layout component
 * 
 * Generic mobile sticky footer with:
 * - Fixed positioning at bottom of viewport
 * - Gradient background with blur effect
 * - iOS safe area support
 * - Primary action (left) + secondary actions (right) layout
 * 
 * Domain-agnostic - no business logic, just layout.
 * 
 * @example
 * ```tsx
 * <StickyFooter
 *   primaryAction={<Button>Submit</Button>}
 *   secondaryActions={
 *     <>
 *       <IconButton icon={<ShareIcon />} />
 *       <IconButton icon={<HeartIcon />} />
 *     </>
 *   }
 * />
 * ```
 */
export function StickyFooter({
  primaryAction,
  secondaryActions,
  className,
}: StickyFooterProps) {
  return (
    <Container
      className={`${styles.component} ${className || ''}`}
    >
      <FlexContainer
        direction="row"
        align="center"
        justify="space-between"
        className={styles.container}
      >
        {primaryAction}

        {secondaryActions && (
          <FlexContainer
            direction="row"
            gap="6px"
            className={styles.secondaryActions}
          >
            {secondaryActions}
          </FlexContainer>
        )}
      </FlexContainer>
    </Container>
  );
}

export default StickyFooter;
